import React, {useEffect, useRef, useState} from "react";
import {Link, Redirect, useParams} from "react-router-dom";
import ProfileHeader from "../Headers/ProfileHeader";
import {Button, FormLabel, InputGroup, Spinner} from "react-bootstrap";
import {EditableText, EditableTextArea} from "../Shared/EditableWidgets";
import {
  destroyTeam,
  getSignedUrl,
  getTeamInvitedUsers,
  getTeamUsers,
  postTeam,
  putTeam,
  uploadFile
} from "../../lib/api";
import {EditButton, TrashButton} from "../Shared/Buttons";
import ListTeamUsers from "./ListTeamUsers";
import {ErrorMessageType} from "../../lib/types";
import FormFileInput from "react-bootstrap/FormFileInput";
import {FileDrop} from "react-file-drop";
import ListTeamInvitedUsers from "./ListTeamInvitedUsers";
import * as Icon from "react-bootstrap-icons";
import ConfirmDelete from "../Shared/ConfirmDelete";

// Team or Org Admin can edit team name and image and logo
// Team or Org Admin can remove or block people
// Team or Org Admin can make someone else be an Org or Team admin
// Org or team members can add people as members or guest
// Org or team members or guests can bookmark and un-bookmark a Team that they can see
// Anyone can add a new Org and they become admin
// Anyone can add a new Team to an org and they are automatically admin of that Team but so is the Org admin

// If it's a new Team then don't save information until hit a Create Team button that only appears on new
// Otherwise always save information as change each piece of information, name image, etc
// Don't show Create Organization button unless super-admin

function TeamView(props: { cognitoUser: any, realvrUser: any, loadData: any }) {
  const {cognitoUser, realvrUser, loadData} = props;
  // @ts-ignore
  const {orgId, teamId} = useParams();
  const {orgs} = realvrUser.user;
  const org = orgs?.find((org: any) => org.id === orgId)
  let currentPath: string;
  let team: any;
  if (teamId === 'new') {
    team = {
      name: '',
      description: '',
      imageUrl: 'https://app.villa.rocks/default_hq.png',
      scopes: ['team_user_member']
    };
    currentPath = `/org/`;
  } else {
    team = org?.teams.find((team: any) => team.id === teamId)
    currentPath = `/org/${org?.id}`;
  }

  useEffect(() => {
      if (teamId === 'new') {
        return
      }

      fetchUsers();
    }, []
  )

  function fetchUsers() {
    fetchTeamUsers();
    fetchTeamInvitedUsers();
  }

  function fetchTeamUsers() {
    getTeamUsers(team).then(response => {
      if (response.ok) {
        response.json().then(json => {
          setUsers(json["teamUsers"]);
        });
      }
    });
  }

  function fetchTeamInvitedUsers() {
    getTeamInvitedUsers(team).then(response => {
      if (response.ok) {
        response.json().then(json => {
          setInvitedUsers(json["teamInvitedUsers"]);
        });
      }
    });
  }

  const [redirectToPath, setRedirectToPath] = useState(currentPath);
  const [isEditing, setIsEditing] = useState(teamId === 'new');
  const [error, setError] = useState<null | ErrorMessageType>(null);
  const [name, setName] = useState(team?.name || '');
  const [prompt, setPrompt] = useState(team?.prompt || '');
  const [users, setUsers] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [description, setDescription] = useState(team?.description || '');
  // const [filesUpload, setFilesUpload] = useState([]);
  const [isUploading, setIsuploading] = useState(false);
  const [showConfirmDestroy, setShowConfirmDestroy] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null)

  function onTargetClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function onDrop(args: any) {
    // setFilesUpload(args);
    uploadFiles(args);
  }

  function uploadProgress(progress: number, total: number) {
    console.log('progress', progress, 'total', total)
  }

  function handleFileInput(e: any) {
    // setFilesUpload(e.target.files);
    uploadFiles(e.target.files);
  }

  function uploadFiles(files: any) {
    if (!permissionAdmin) {
      setError({message: "You are not an Admin of this Villa"});
      return;
    }
    if (!team.id) {
      setError({message: "Please save new team first"});
      return;
    }
    setError(null);
    const file: any = files[0];
    if (!file.type.startsWith("image/")) {
      setError({message: "Please upload a png or jpeg image"});
      return
    }
    setIsuploading(true);
    getSignedUrl(file).then((response) => {
      response.json().then(json => {
        // setFileMessage(`Uploading file ${file.name} ${i+1} of ${files.length}`)
        uploadFile(json.postUrl, file, uploadProgress)
          .then(uploadResponse => {
            putTeam({imageUrl: json.getUrl, imageAttributes: {}}, org.id, team.id).then(teamResponse => {
              setIsuploading(false);
              if (teamResponse.ok) {
                loadData();
              } else {
                teamResponse.json().then(json => {
                  console.log("error json", json);
                  setError(json?.error);
                });
              }
            })
          }).catch(error => {
          setIsuploading(false);
          setError({message: "Error uploading"})
        })
      })
    });
  }

  // Check if org and team id exists otherwise switch to /home
  if (!org || (!team && teamId !== 'new')) {
    return <Redirect to="/home"/>
  }

  const permissionAdmin = !!org.scopes.find((scope: string) => scope === "org_user_admin");
  const permissionMember = !!team.scopes.find((scope: string) => scope === "team_user_member");
  const permissionGuest = org.scopes.find((scope: string) => scope === "org_user_guest")
    || !!team.scopes.find((scope: string) => scope === "team_user_guest");
  const permissionAdminOrMember = permissionAdmin || permissionMember;

  function saveSuccess() {
    setError(null);
    setIsEditing(false);
    loadData().then(() => {
      const path = `/org/${orgId}/villas`;
      setRedirectToPath(path);
    });
  }

  function cancel() {
    setName(team.name);
    setDescription(team.description);
    setError(null);
    setIsEditing(false);
    if (teamId === 'new') {
      const path = `/org/${orgId}/villas`;
      setRedirectToPath(path);
    }
  }

  function saveError(json: any) {
    setError(json?.error);
  }

  function save() {
    const attributes = {name, description, prompt, imageUrl};
    const method = team.id ? putTeam : postTeam;
    method(attributes, org.id, team.id)
      .then((response) => {
        if (response.ok) {
          saveSuccess();
        } else {
          response.json().then(json => {
            saveError(json);
          })
        }
      })
      .catch((err: any) => {
        console.log(err)
        saveError({message: 'An error occurred, please try again'});
      });
  }

  function confirmDestroy() {
    setShowConfirmDestroy(true);
  }

  function hideConfirmDestroy() {
    setShowConfirmDestroy(false);
  }

  function destroy() {
    setShowConfirmDestroy(false);
    destroyTeam(org.id, team.id)
      .then((response) => {
        if (response.ok) {
          loadData().then(() => {
            const path = `/org/${orgId}/villas`;
            console.error(path)
            setRedirectToPath(path);
          });
        } else {
          response.json().then(json => {
            saveError(json);
          });
        }
      })
  }

  const title = (team.id ? '' : 'New ') + "Villa";
  const orgLink = `/orgs/${org.id}`;

  const buttons = isEditing
    ? <div className="col-12 py-1 text-right">
      <TrashButton onClick={() => confirmDestroy()} visible={permissionAdmin}/>&nbsp;
      <Button
        onClick={cancel}
        variant="secondary">
        Cancel
      </Button>
      &nbsp;
      <Button
        onClick={save}
        variant="primary">
        Save
      </Button>
    </div>
    : <div className="col-12 text-right">
      <TrashButton onClick={() => confirmDestroy()} visible={permissionAdmin}/>
      <EditButton onClick={() => setIsEditing(true)} visible={permissionAdmin && !isEditing}/>
    </div>

  const errorMessage =
    error ?
      <div className="row pb-3 error">
        <div className="col-12 text-center">
          {(error as any)?.message}
        </div>
      </div>
      : null;

  if (redirectToPath !== currentPath) {
    console.log('Redirecting to ' + redirectToPath + ' from ' + currentPath)
    return <Redirect to={redirectToPath}/>
  }

  // const imageStyle = {
  //   backgroundImage: `url("${team.imageUrl}")`,
  //   backgroundSize: "cover"
  // };

  const linkToHome = `/org/${org.id}/villas`;
  const homeImage = org.imageUrl;

  const imageUrl = team.imageUrl || team.imageAttributes?.urls?.thumb;

  function bundle(parcel: any) {
    if (parcel.bundledAssetSkus === "LUXURY_VILLA_1") {
      return <span className="text-nowrap">+ Luxury Villa</span>;
    } else if (parcel.bundledAssetSkus === "LUXURY_YACHT_1") {
      return <span className="text-nowrap">+ Luxury Yacht</span>;
    } else {
      return "";
    }
  }

  return (
    <>
      <ConfirmDelete title={`Delete Villa ${team.name}?`}
                     confirm={destroy}
                     show={showConfirmDestroy}
                     onHide={hideConfirmDestroy}
      />
      <ProfileHeader cognitoUser={cognitoUser} title={title} linkToHome={linkToHome} image={homeImage}/>
      <InputGroup>
        <section className="container main view-team rounded pt-1 pb-4 mt-0 mt-md-3">
          {errorMessage}
          <div className="row">
            {buttons}
          </div>
          <div className="row px-3 py-0 d-md-none">
            <div className="col-md-12 px-0 py-0">
              <FormLabel className="mb-0 h-auto">Organization</FormLabel>
            </div>
          </div>
          <div className="row px-3 py-2">
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Organization</FormLabel>
            </div>
            <div className="col-4 px-0 editable">
              <Link to={orgLink}>{org.name}</Link>
            </div>
          </div>
          {team.parcel != null ?
            <>
              <div className="row px-3 py-0 d-md-none">
                <div className="col-md-12 px-0 py-0">
                  <FormLabel className="mb-0 h-auto">Land</FormLabel>
                </div>
              </div>
              <div className="row px-3 py-2">
                <div className="d-none d-md-block col-md-2 text-right">
                  <FormLabel>Land</FormLabel>
                </div>
                <div className="col-6 px-0 editable text-no-wrap">
                  {team.parcel.planet.name} #{team.parcel.num}&nbsp;<Icon.HexagonFill size={24} color="#83362B"/><span
                  className="hexagon"/>&nbsp;{bundle(team.parcel)}
                </div>
              </div>
            </>
            : null}
          <div className="row px-3 py-0 d-md-none">
            <div className="col-md-12 px-0 py-0">
              <FormLabel className="mb-0 h-auto">Name</FormLabel>
            </div>
          </div>
          <div className="row px-3 py-2">
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Name</FormLabel>
            </div>
            <div className="col-12 col-md-4 px-0">
              <div className="row">
                <div className="col">
                  <EditableText
                    value={name}
                    maxLength={40}
                    onChange={(event: any) => setName(event.target.value)}
                    isEditable={permissionAdmin}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    placeholder="Name, i.e. Partnerships, C-Suite, HR"
                    ariaLabel="Name"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row px-3 pt-2 d-md-none">
            <div className="col-md-12 px-0 py-0">
              <FormLabel className="mb-0 h-auto">Tagline</FormLabel>
            </div>
          </div>
          <div className="row px-3 py-2">
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Tagline</FormLabel>
            </div>
            <div className="col-12 col-md-4 px-0">
              <div className="row">
                <div className="col">
                  <EditableTextArea
                    value={description}
                    maxLength={120}
                    rows={3}
                    onChange={(event: any) => setDescription(event.target.value)}
                    isEditable={permissionAdmin}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    placeholder="Tagline, i.e. The raddest deals are made here!"
                    ariaLabel="Tagline"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row px-3 pt-2 pb-0 d-md-none">
            <div className="col-md-12 px-0 pb-0">
              <FormLabel className="mb-0 h-auto">Mascot</FormLabel>
            </div>
          </div>
          <div className="row px-3 py-2">
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Mascot</FormLabel>
            </div>
            <div className="col-12 col-md-4 px-0">
              <FormFileInput className="d-none"
                             ref={fileInputRef}
                             type="file"
                             onChange={handleFileInput}
                             accept="image/*"
                             multiple/>
              {imageUrl ?
                <FileDrop className={`border ${isUploading ? 'file-drop' : ''}`} onTargetClick={onTargetClick}
                          onDrop={onDrop}>
                  {isUploading ? <Spinner animation="border"/> : <img src={imageUrl} alt={team.name} width="100%"/>}
                </FileDrop>
                :
                <FileDrop className="file-drop border" onTargetClick={onTargetClick} onDrop={onDrop}>
                  {isUploading ? <Spinner animation="border"/> : "Drag & drop image or click"}
                </FileDrop>
              }
            </div>
          </div>
          <div className="row px-3 pt-2 pb-0 d-md-none">
            <div className="col-md-12 px-0 pb-0">
              <FormLabel className="mb-0 h-auto">Members</FormLabel>
            </div>
          </div>
          <div className="row px-3 py-2">
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Members</FormLabel>
            </div>
            <div className="col-12 col-md-8 px-0">
              <ListTeamUsers
                org={org}
                team={team}
                users={users}
                permissionGuest={permissionGuest}
                permissionAdmin={permissionAdmin}
                loadData={fetchUsers}/>
            </div>
          </div>
          <div className="row px-3 py-2">
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Invited</FormLabel>
            </div>
            <div className="col-12 col-md-8 px-0">
              <ListTeamInvitedUsers
                org={org}
                team={team}
                invitedUsers={invitedUsers}
                permissionAdmin={permissionAdmin}
                loadData={fetchUsers}/>
            </div>
          </div>
          <div className="row px-3 py-2">
            <div className="row px-3 py-0 d-md-none">
              <div className="col-md-12 px-0 py-0">
                <FormLabel className="mb-0 h-auto">Assets</FormLabel>
              </div>
            </div>
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Assets</FormLabel>
            </div>
            <div className="col-12 col-md-8 px-0 pt-2">
              <Link className="underline" to={`/org/${orgId}/team/${teamId}/assets`}>
                View 2D and 3D Assets in this Villa
              </Link>
            </div>
          </div>
          {team.parcel != null ?
            <>
              <div className="row px-3 pt-3 d-md-none">
                <div className="col-md-12 px-0 py-0">
                  <FormLabel className="mb-0 h-auto">Purchase History</FormLabel>
                </div>
              </div>
              <div className="row px-3 py-2">
                <div className="d-none d-md-block col-md-2 text-right">
                  <FormLabel>Purchase History</FormLabel>
                </div>
                <div className="col-10 px-0 editable">
                  {team.parcel.parcelLedgers.map((ledger: any) => (
                    <div className="row">
                      <div className="col">{ledger}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
            : null}

          <div className="row px-3 py-2">
            <div className="d-none d-md-block col-md-2 text-right">
              <FormLabel>Customize AI</FormLabel>
            </div>
            <div className="col-12 col-md-4 px-0">
              <div className="row">
                <div className="col">
                  <EditableTextArea
                    value={prompt}
                    maxLength={16000}
                    rows={10}
                    onChange={(event: any) => setPrompt(event.target.value)}
                    isEditable={permissionAdmin}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    placeholder="You can paste up to 16,000 characters of text here to customize how your AI assistant responds in this Villa"
                    ariaLabel="Customize AI"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            {buttons}
          </div>
        </section>
      </InputGroup>
    </>
  );
}

export default TeamView;
